// API calls for a booking's documents (AB#609): GET api/ams/bookings/{id}/documents and DELETE .../{documentId} through
// RTK Query; the multipart upload and the streamed download through the existing lib/api.ts helpers, which handle
// FormData and a file save. Plus the small pure helpers the documents section uses.
import { ApiError, api as restApi } from "@/lib/api"
import { extractApiError } from "@/lib/api-errors"
import { api } from "@/services/api"
import { formatNoteTime } from "./notesApi"

/** Where a document stands with the malware scanner. Only a Clean one can be downloaded. */
export type DocumentScanStatus = "Pending" | "Clean" | "Malicious" | "Failed"

/** One document on a booking, as the staff endpoint lists it. Never a URL: content is only streamed. */
export interface BookingDocument {
  id: string
  /** The sanitized original name. */
  fileName: string
  sizeBytes: number
  /** What the server recognised the bytes as: application/pdf or text/plain. */
  contentType: string
  /** A DocumentScanStatus; typed loosely so a value this build does not know is still shown safely. */
  scanStatus: DocumentScanStatus | string
  uploadedByUpn: string
  uploadedAtUtc: string
  /** True only once the scan has cleared it. */
  canDownload: boolean
}

export interface BookingDocumentList {
  items: BookingDocument[]
}

/** The file types the upload accepts, for the file input. The server decides from the bytes either way. */
export const ACCEPTED_DOCUMENT_TYPES = ".pdf,.txt"

const taggedApi = api.enhanceEndpoints({ addTagTypes: ["BookingDocuments"] })

/** The documents of one booking request. */
export function documentsPath(requestId: string): string {
  return `/api/ams/bookings/${encodeURIComponent(requestId)}/documents`
}

/** One document's streamed content. */
export function documentContentPath(requestId: string, documentId: string): string {
  return `${documentsPath(requestId)}/${encodeURIComponent(documentId)}/content`
}

export const documentsApi = taggedApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * The booking's documents, newest first. 404 when the documents flag is off or the booking is
     * unknown; 403 for a viewer who neither hosts the booking nor manages appointments.
     */
    getBookingDocuments: builder.query<BookingDocumentList, string>({
      query: (requestId) => ({ url: documentsPath(requestId) }),
      providesTags: (_result, _error, requestId) => [{ type: "BookingDocuments" as const, id: requestId }],
    }),
    /** Removes a document in any scan status. */
    deleteBookingDocument: builder.mutation<void, { requestId: string; documentId: string }>({
      query: ({ requestId, documentId }) => ({
        url: `${documentsPath(requestId)}/${encodeURIComponent(documentId)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { requestId }) => [{ type: "BookingDocuments" as const, id: requestId }],
    }),
  }),
})

export const { useGetBookingDocumentsQuery, useDeleteBookingDocumentMutation } = documentsApi

/** The action that makes the list refetch after an upload. */
export function invalidateBookingDocuments(requestId: string) {
  return documentsApi.util.invalidateTags([{ type: "BookingDocuments", id: requestId }])
}

/** The HTTP status an RTK Query error carries, if any. */
export function errorStatus(error: unknown): number | null {
  const status = (error as { status?: unknown } | null | undefined)?.status
  return typeof status === "number" ? status : null
}

/**
 * Whether the section should draw nothing for this list error: 404 (the documents flag is off, or
 * CMS has no such booking) and 403 (the caller neither hosts the booking nor manages appointments).
 */
export function hidesDocumentsSection(error: unknown): boolean {
  const status = errorStatus(error)
  return status === 404 || status === 403
}

const sizeUnits = ["KB", "MB", "GB"]

/** A size people read: bytes below 1 KB, then KB/MB/GB with one decimal under 10. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return ""
  if (bytes < 1024) return `${bytes} B`
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < sizeUnits.length - 1) {
    value /= 1024
    unit++
  }
  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value)
  return `${rounded} ${sizeUnits[unit]}`
}

/** When a document was uploaded, in the sites' own time zone (Pacific). */
export const formatDocumentTime = formatNoteTime

/**
 * What the server stores as the uploader of a document the customer sent through their booking link
 * (PublicBookingDocumentAccess.CustomerUploaderUpn). Not a person's sign-in, so it is never shown raw.
 */
export const CUSTOMER_UPLOADER_UPN = "public:customer"

/** Who uploaded a document, as staff read it: the customer's uploads by name of channel, a staff UPN as is. */
export function formatUploader(uploadedByUpn: string): string {
  return uploadedByUpn === CUSTOMER_UPLOADER_UPN ? "Customer (from their link)" : uploadedByUpn
}

/** The badge a document's scan status earns, or null for none (Clean is the normal state). */
export function scanStatusBadge(status: string): { label: string; title: string; variant: "secondary" | "destructive" | "outline" } | null {
  switch (status) {
    case "Clean":
      return null
    case "Pending":
      return { label: "Scanning", title: "Being checked for malware. It can be downloaded once the scan finishes.", variant: "secondary" }
    case "Failed":
      return { label: "Scan failed", title: "The malware scan did not finish. Remove it and upload it again.", variant: "destructive" }
    default:
      return { label: "Unavailable", title: "This document cannot be downloaded.", variant: "outline" }
  }
}

/**
 * The documents worth showing. A Malicious one is deleted by the scanner moments after it is marked,
 * so it is never listed as a row staff could act on.
 */
export function visibleDocuments(documents: readonly BookingDocument[]): BookingDocument[] {
  return documents.filter(document => document.scanStatus !== "Malicious")
}

/** The status of a failed lib/api.ts call: an ApiError's own, else null (a network failure). */
export function failureStatus(error: unknown): number | null {
  return error instanceof ApiError ? error.status : null
}

/**
 * The fallback for a refused upload whose response carried no message of its own. Each one stands for
 * every code the server sends with that status (400: disallowedType, tooMany, notBooked or missingFile).
 */
export function uploadErrorMessage(status: number | null): string {
  switch (status) {
    case 400:
      return "This file was not accepted. Only PDF and plain-text files can be added, to a booked appointment, up to its document limit."
    case 403:
      return "You do not have permission to add documents to this appointment."
    case 404:
      return "This appointment can no longer take documents."
    case 413:
      return "The file is larger than the largest document allowed."
    case 502:
      return "The file could not be stored. Try again."
    default:
      return "The file could not be uploaded."
  }
}

/**
 * What staff are told when an upload is refused: the server's own message (`{ code, message }`) when it
 * sent one, so each refusal reads differently, else the fallback for its status.
 */
export function uploadFailureMessage(error: unknown): string {
  const serverMessage = error instanceof ApiError ? extractApiError(error.body).trim() : ""
  return serverMessage || uploadErrorMessage(failureStatus(error))
}

/** What staff are told when a download is refused. */
export function downloadErrorMessage(status: number | null): string {
  switch (status) {
    case 409:
      return "This document is still being scanned, or its scan failed, so it cannot be downloaded."
    case 403:
      return "You do not have permission to download this document."
    case 404:
      return "This document is no longer available."
    case 502:
      return "The file could not be read. Try again."
    default:
      return "The document could not be downloaded."
  }
}

/** The lib/api.ts helpers the upload and download use; a parameter so tests can stand in for them. */
export interface DocumentTransfer {
  upload: <T>(path: string, formData: FormData) => Promise<T>
  download: (path: string, fallbackFilename?: string, expectedContentType?: string) => Promise<void>
}

export type UploadOutcome = { ok: true; document: BookingDocument } | { ok: false; message: string }

/**
 * Uploads one file to the booking through lib/api.ts and, when it is stored, marks the list stale so
 * it refetches with the new (Pending) document.
 */
export async function uploadBookingDocument(
  requestId: string,
  file: File,
  dispatch: (action: ReturnType<typeof invalidateBookingDocuments>) => unknown,
  transfer: DocumentTransfer = restApi,
): Promise<UploadOutcome> {
  const form = new FormData()
  form.append("file", file, file.name)
  try {
    const document = await transfer.upload<BookingDocument>(documentsPath(requestId), form)
    dispatch(invalidateBookingDocuments(requestId))
    return { ok: true, document }
  } catch (error) {
    return { ok: false, message: uploadFailureMessage(error) }
  }
}

/** Streams one document to a file save; null when it worked, else what to tell staff. */
export async function downloadBookingDocument(
  requestId: string,
  document: Pick<BookingDocument, "id" | "fileName" | "contentType">,
  transfer: DocumentTransfer = restApi,
): Promise<string | null> {
  try {
    await transfer.download(documentContentPath(requestId, document.id), document.fileName, document.contentType)
    return null
  } catch (error) {
    return downloadErrorMessage(failureStatus(error))
  }
}
